import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClientStore } from '@/stores/client';

export interface PerformanceContextValue {
  clientUid: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  setDateRange: (range: { from: string | null; to: string | null }) => void;
  setLast7Days: () => void;
  setLast28Days: () => void;
  setLast90Days: () => void;
  setLifetime: () => void;
}

const PerformanceContext = createContext<PerformanceContextValue | null>(null);

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function lastNDays(days: number): { from: string; to: string } {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - days);
  return { from: isoDate(start), to: isoDate(today) };
}

export function PerformanceProvider({ children }: { children: ReactNode }) {
  const clientUid = useClientStore((s) => s.selectedClientUid);
  const [searchParams, setSearchParams] = useSearchParams();

  const dateFrom = searchParams.get('date_from');
  const dateTo = searchParams.get('date_to');

  const setDateRange = useCallback(
    ({ from, to }: { from: string | null; to: string | null }) => {
      const next = new URLSearchParams(searchParams);
      if (from) next.set('date_from', from);
      else next.delete('date_from');
      if (to) next.set('date_to', to);
      else next.delete('date_to');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Default to last 28 days when neither param is present.
  useEffect(() => {
    if (!dateFrom && !dateTo) {
      const { from, to } = lastNDays(28);
      setDateRange({ from, to });
    }
    // run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLast7Days = useCallback(() => {
    const { from, to } = lastNDays(7);
    setDateRange({ from, to });
  }, [setDateRange]);
  const setLast28Days = useCallback(() => {
    const { from, to } = lastNDays(28);
    setDateRange({ from, to });
  }, [setDateRange]);
  const setLast90Days = useCallback(() => {
    const { from, to } = lastNDays(90);
    setDateRange({ from, to });
  }, [setDateRange]);
  const setLifetime = useCallback(() => {
    setDateRange({ from: null, to: null });
  }, [setDateRange]);

  const value = useMemo<PerformanceContextValue>(
    () => ({
      clientUid,
      dateFrom,
      dateTo,
      setDateRange,
      setLast7Days,
      setLast28Days,
      setLast90Days,
      setLifetime,
    }),
    [clientUid, dateFrom, dateTo, setDateRange, setLast7Days, setLast28Days, setLast90Days, setLifetime],
  );

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
}

export function usePerformanceContext(): PerformanceContextValue {
  const ctx = useContext(PerformanceContext);
  if (!ctx) {
    throw new Error(
      'usePerformanceContext must be used within a PerformanceProvider',
    );
  }
  return ctx;
}
